import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Title,
    Loader,
    Alert,
    Paper,
    Table,
    Text,
    Badge,
    Select,
    Group,
    Stack
} from '@mantine/core';
import { SchemaAttributeTableView } from '../../common/SchemaAttributeTableView';
import { Tier2SchemaTabView } from '../../common/Tier2SchemaTabView';
import { getSingularLabel } from '../../../utils/pluralizationUtils';
import EntitlementsService from '../../../services/EntitlementsService';
import {
    buildFormAttributesFromTier2Attributes,
    buildFormAttributesFromTier2AttributeBindings,
    isEntitySetFacade,
    type FormAttributeOption
} from '../../../types/EntityEntitlements';

export interface SchemaAttributeItem {
    Id?: string;
    name?: string;
    Name?: string;
    type?: string;
    Type?: string;
    required?: boolean;
    Required?: boolean;
    multiValued?: boolean;
    MultiValued?: boolean;
}

export type SchemaTabViewMode = 'fieldOptions' | 'apiAttributes' | 'tier2';

/** Minimal entity type option (e.g. from EntityTypesContext) */
export interface EntityTypeOption {
    name: string;
    endpoint?: string;
    schema?: string;
    description?: string;
}

interface EntityDetailSchemaTabProps {
    schemaAttributes: SchemaAttributeItem[];
    schemaLoading: boolean;
    /** Entity type we are displaying (e.g. Set, Person). Used for the entity-type selector default. */
    entityType?: string;
    /** When provided, show entity-type selector and view-mode selector to show Field options / API attributes / Tier2. */
    entityTypes?: EntityTypeOption[] | null;
    /** Pass true when used inside entity detail dialog (enables API attributes view). */
    authResolved?: boolean;
}

const VIEW_MODE_OPTIONS: { value: SchemaTabViewMode; label: string }[] = [
    { value: 'fieldOptions', label: 'Field options' },
    { value: 'apiAttributes', label: 'API attributes' },
    { value: 'tier2', label: 'Tier2 entitlement' }
];

function mapFormAttributesToSchemaAttributes(attrs: FormAttributeOption[]): SchemaAttributeItem[] {
    return (attrs ?? []).map((a) => ({
        Id: a.id ?? a.name,
        Name: a.label ?? a.name,
        Type: a.type ?? 'string',
        Required: a.required === true,
        MultiValued: a.multiValued === true
    }));
}

export const EntityDetailSchemaTab: React.FC<EntityDetailSchemaTabProps> = ({
    schemaAttributes,
    schemaLoading,
    entityType = 'Entity',
    entityTypes,
    authResolved = true
}) => {
    // Normalize to lowercase so it matches entityTypeOptions (value: t.name.toLowerCase())
    const defaultScope = (entityType || '').toString().trim().toLowerCase();
    const [selectedSchemaEntityType, setSelectedSchemaEntityType] = useState<string>(defaultScope);
    const [viewMode, setViewMode] = useState<SchemaTabViewMode>('fieldOptions');

    // When dialog entity (or filter scope) changes, sync selected type to current entity's type
    useEffect(() => {
        setSelectedSchemaEntityType(defaultScope);
    }, [defaultScope]);

    // Field options: derived from tier2 (same source as upsert form / AttributeSelector).
    const [fieldOptionsAttrs, setFieldOptionsAttrs] = useState<SchemaAttributeItem[]>([]);
    const [fieldOptionsLoading, setFieldOptionsLoading] = useState(false);

    const loadFieldOptionsForType = useCallback(async (typeKey: string) => {
        const normalized = (typeKey ?? '').toString().trim();
        if (!normalized) {
            setFieldOptionsAttrs([]);
            return;
        }

        setFieldOptionsLoading(true);
        try {
            const cached = EntitlementsService.getCachedEntityEntitlementForSchemaEntity(normalized);
            const tier2 = cached ?? (await EntitlementsService.fetchEntityEntitlementForSchemaEntity(normalized));

            const formAttrs = isEntitySetFacade(tier2)
                ? buildFormAttributesFromTier2Attributes(tier2?.Attributes)
                : (buildFormAttributesFromTier2AttributeBindings(tier2?.AttributeBindings) ?? buildFormAttributesFromTier2Attributes(tier2?.Attributes));
            setFieldOptionsAttrs(mapFormAttributesToSchemaAttributes(formAttrs));
        } catch (err) {
            console.error('Error loading tier2 field options for type:', normalized, err);
            setFieldOptionsAttrs([]);
        } finally {
            setFieldOptionsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (viewMode !== 'fieldOptions') return;
        loadFieldOptionsForType(selectedSchemaEntityType);
    }, [viewMode, selectedSchemaEntityType, loadFieldOptionsForType]);

    // Back-compat: if tier2 comes back empty, fall back to hook-provided schemaAttributes (old Set FilterScope path).
    const displayAttributes = viewMode === 'fieldOptions'
        ? (fieldOptionsAttrs.length > 0 ? fieldOptionsAttrs : schemaAttributes)
        : [];
    const displayLoading = viewMode === 'fieldOptions'
        ? (fieldOptionsLoading || (schemaLoading && fieldOptionsAttrs.length === 0))
        : false;

    const entityTypeOptions = useMemo(() => {
        if (!entityTypes?.length) return [];
        const items = entityTypes
            .filter(t => t.name.toLowerCase() !== 'schemaentity' && t.name.toLowerCase() !== 'schemaattribute')
            .map(t => ({ value: t.name.toLowerCase(), label: getSingularLabel(t.name) }));
        return items.sort((a, b) => a.label.localeCompare(b.label));
    }, [entityTypes]);

    const showSelectors = entityTypeOptions.length > 0;

    const fieldOptionsContent = (
        <Box>
            <Title order={5} mb="xs">
                Schema attributes for {selectedSchemaEntityType || '—'}
            </Title>
            {displayLoading ? (
                <Group justify="center" align="center" mih={200}>
                    <Loader />
                </Group>
            ) : displayAttributes.length > 0 ? (
                <Paper withBorder p="xs" mb="sm">
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th><strong>Name</strong></Table.Th>
                                <Table.Th><strong>Type</strong></Table.Th>
                                <Table.Th><strong>Required</strong></Table.Th>
                                <Table.Th><strong>Multi-valued</strong></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {displayAttributes.map((attr, index) => (
                                <Table.Tr key={attr.Id || attr.name || index}>
                                    <Table.Td>
                                        <Text size="sm" ff="monospace">
                                            {attr.Name || attr.name}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge size="sm" variant="outline">
                                            {attr.Type || attr.type || 'string'}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge size="sm" color={attr.Required || attr.required ? 'red' : 'gray'}>
                                            {attr.Required || attr.required ? 'Yes' : 'No'}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge size="sm" color={attr.MultiValued || attr.multiValued ? 'blue' : 'gray'}>
                                            {attr.MultiValued || attr.multiValued ? 'Yes' : 'No'}
                                        </Badge>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Paper>
            ) : (
                <Alert color="blue" mt="md">
                    No schema attributes found for {selectedSchemaEntityType || '—'}
                </Alert>
            )}
        </Box>
    );

    return (
        <Stack gap="md">
            {showSelectors && (
                <Group align="flex-end" wrap="nowrap" gap="md">
                    <Select
                        label="Entity type"
                        description="Schema shown for this type"
                        placeholder="Select type..."
                        data={entityTypeOptions}
                        value={selectedSchemaEntityType || null}
                        onChange={(v) => setSelectedSchemaEntityType(v ?? '')}
                        clearable={false}
                        allowDeselect={false}
                        maw={220}
                    />
                    <Select
                        label="View"
                        description="How schema is loaded"
                        data={VIEW_MODE_OPTIONS}
                        value={viewMode}
                        onChange={(v) => v && setViewMode(v as SchemaTabViewMode)}
                        maw={180}
                    />
                </Group>
            )}

            {(!showSelectors || viewMode === 'fieldOptions') && fieldOptionsContent}

            {showSelectors && viewMode === 'apiAttributes' && (
                <Box>
                    {selectedSchemaEntityType ? (
                        <SchemaAttributeTableView
                            entityType="schemaentity"
                            selectedEntityType={selectedSchemaEntityType}
                            authResolved={authResolved}
                        />
                    ) : (
                        <Alert color="blue">
                            Select an entity type above to view API schema attributes.
                        </Alert>
                    )}
                </Box>
            )}

            {showSelectors && viewMode === 'tier2' && (
                <Box>
                    {selectedSchemaEntityType ? (
                        <Tier2SchemaTabView entityType={selectedSchemaEntityType} />
                    ) : (
                        <Alert color="blue">
                            Select an entity type above to view Tier2 entitlement.
                        </Alert>
                    )}
                </Box>
            )}
        </Stack>
    );
};
